import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  clearPendingSelection,
  formatMoney,
  getPendingSelection,
  markDismissed,
  markShownThisSession,
} from '../utils/choosePlan';
import { DIAL_CODES, defaultDialCode } from '../utils/dialCodes';
import './ChoosePlanModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface CatalogPlanLimit {
  featureKey: string;
  label: string;
  maxPerMonth: number | null;
}

interface CatalogPlan {
  id: string;
  name: string;
  priceMonthly: number;
  currency?: 'INR' | 'USD';
  price?: number;
  isRecommended?: boolean;
  limits: CatalogPlanLimit[];
}

interface CatalogPaygItem {
  featureKey: string;
  label: string;
  unit: string;
  pricePerUnit: number;
  currency?: 'INR' | 'USD';
  price?: number;
}

interface CatalogResponse {
  plans: CatalogPlan[];
  payg: CatalogPaygItem[];
  viewerCountry?: string | null;
}

type Tab = 'plans' | 'payg';

const ChoosePlanModal: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(() => (getPendingSelection()?.kind === 'payg' ? 'payg' : 'plans'));
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // Phone step: accounts with no saved phone (e.g. Google sign-in) add one before seeing prices,
  // since a +91 number is what switches pricing to INR.
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [dialCode, setDialCode] = useState('+91');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const needsPhone = Boolean(user) && !user?.phone && !phoneSaved;

  // Opening the prompt counts as "shown" so revisiting /dashboard does not reopen it this session.
  useEffect(() => {
    if (user?.id) markShownThisSession(user.id);
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalogRes, meRes] = await Promise.all([
          fetch(`${API_BASE}/api/pricing/catalog`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/me/pricing`, { credentials: 'include' }),
        ]);
        if (!catalogRes.ok) throw new Error('catalog');
        const data = (await catalogRes.json()) as CatalogResponse;
        const me = meRes.ok ? ((await meRes.json()) as { assignedPlan?: { id: string } | null }) : null;
        if (cancelled) return;
        setCatalog(data);
        setDialCode(defaultDialCode(data.viewerCountry));
        setCurrentPlanId(me?.assignedPlan?.id ?? null);

        const pending = getPendingSelection();
        const pendingPlan = pending?.kind === 'plan' ? data.plans.find((p) => p.id === pending.planId) : undefined;
        setSelectedPlanId(
          pendingPlan?.id ?? data.plans.find((p) => p.isRecommended)?.id ?? data.plans[0]?.id ?? null
        );
      } catch {
        if (!cancelled) setError('Unable to load plans right now.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const close = React.useCallback(() => {
    if (user?.id) markDismissed(user.id);
    clearPendingSelection();
    navigate('/dashboard', { replace: true });
  }, [navigate, user?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [close]);

  const selectedPlan = useMemo(
    () => catalog?.plans.find((p) => p.id === selectedPlanId) ?? null,
    [catalog, selectedPlanId]
  );

  const phoneDigits = phoneInput.replace(/\D/g, '').replace(/^0+/, '');

  const savePhone = async () => {
    setPhoneSaving(true);
    setPhoneError(null);
    try {
      const res = await fetch(`${API_BASE}/api/me/phone`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: `${dialCode}${phoneDigits}` }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || 'Could not save your phone number.');
      }
      // Prices depend on the saved phone (+91 -> INR), so reload the catalog before showing plans.
      const catalogRes = await fetch(`${API_BASE}/api/pricing/catalog`, { credentials: 'include' });
      if (catalogRes.ok) setCatalog((await catalogRes.json()) as CatalogResponse);
      setPhoneSaved(true);
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Could not save your phone number.');
    } finally {
      setPhoneSaving(false);
    }
  };

  const canContinue = tab === 'payg' || (selectedPlan !== null && selectedPlan.id !== currentPlanId);

  const handleContinue = () => {
    if (tab === 'payg') {
      navigate('/checkout?mode=payg');
      return;
    }
    if (selectedPlan) navigate(`/checkout?plan=${encodeURIComponent(selectedPlan.id)}`);
  };

  return (
    <div className="cp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="cp-modal" role="dialog" aria-modal="true" aria-labelledby="cp-title">
        <div className="cp-head">
          <div>
            <h2 id="cp-title" className="cp-title">{needsPhone ? 'Add your phone number' : 'Choose how you want to pay'}</h2>
            <p className="cp-sub">
              {needsPhone
                ? 'We use it to show prices in your currency and to reach you about your account.'
                : 'Pick a plan or pay as you go, then complete checkout to start using the features.'}
            </p>
          </div>
          <button type="button" className="cp-close" onClick={close} aria-label="Close">×</button>
        </div>

        {!needsPhone && (
        <div className="cp-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'plans'}
            className={`cp-tab${tab === 'plans' ? ' is-active' : ''}`}
            onClick={() => setTab('plans')}
          >
            Plans
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'payg'}
            className={`cp-tab${tab === 'payg' ? ' is-active' : ''}`}
            onClick={() => setTab('payg')}
          >
            Pay as you go
          </button>
        </div>
        )}

        <div className="cp-body">
          {!catalog && !error && <p className="cp-note">Loading…</p>}
          {error && <p className="cp-error">{error}</p>}

          {catalog && needsPhone && (
            <form
              className="cp-phone"
              onSubmit={(e) => {
                e.preventDefault();
                if (phoneDigits.length >= 6 && !phoneSaving) void savePhone();
              }}
            >
              <label className="cp-phone-label" htmlFor="cp-phone-input">Phone number</label>
              <div className="cp-phone-row">
                <select
                  className="cp-phone-select"
                  aria-label="Country code"
                  value={dialCode}
                  onChange={(e) => setDialCode(e.target.value)}
                >
                  {DIAL_CODES.map((c) => <option key={c.label} value={c.code}>{c.label}</option>)}
                </select>
                <input
                  id="cp-phone-input"
                  className="cp-phone-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="98765 43210"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  autoFocus
                />
              </div>
              {phoneError && <p className="cp-error" role="alert">{phoneError}</p>}
            </form>
          )}

          {catalog && !needsPhone && tab === 'plans' && (
            <div className="cp-plans">
              {catalog.plans.length === 0 && <p className="cp-note">No active plans are available right now.</p>}
              {catalog.plans.map((plan) => {
                const isSelected = plan.id === selectedPlanId;
                return (
                  <button
                    type="button"
                    key={plan.id}
                    className={`cp-plan${isSelected ? ' is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => setSelectedPlanId(plan.id)}
                  >
                    <span className="cp-plan-top">
                      <span className="cp-plan-name">{plan.name}</span>
                      {plan.isRecommended && <span className="cp-badge">Recommended</span>}
                      {plan.id === currentPlanId && <span className="cp-badge cp-badge-muted">Current plan</span>}
                    </span>
                    <span className="cp-plan-price">
                      {formatMoney(plan.currency, plan.price ?? plan.priceMonthly)}
                      <span className="cp-plan-per"> / month</span>
                    </span>
                    <ul className="cp-plan-limits">
                      {plan.limits.slice(0, 4).map((limit) => (
                        <li key={`${plan.id}-${limit.featureKey}`}>
                          {limit.label}: {limit.maxPerMonth === null ? 'Unlimited' : limit.maxPerMonth}
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>
          )}

          {catalog && !needsPhone && tab === 'payg' && (
            <div className="cp-payg">
              <p className="cp-note">No monthly fee. You are charged only for what you use, at these rates.</p>
              <ul className="cp-payg-list">
                {catalog.payg.map((item) => (
                  <li key={item.featureKey} className="cp-payg-row">
                    <span className="cp-payg-label">{item.label}</span>
                    <span className="cp-payg-price">
                      {formatMoney(item.currency, item.price ?? item.pricePerUnit)} / {item.unit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="cp-foot">
          <button type="button" className="cp-later" onClick={close}>Maybe later</button>
          {needsPhone ? (
            <button
              type="button"
              className="cp-continue"
              disabled={!catalog || phoneDigits.length < 6 || phoneSaving}
              onClick={() => void savePhone()}
            >
              {phoneSaving ? 'Saving…' : 'Save and continue'}
            </button>
          ) : (
            <button type="button" className="cp-continue" disabled={!catalog || !canContinue} onClick={handleContinue}>
              {tab === 'payg' ? 'Continue with Pay as you go' : selectedPlan ? `Continue with ${selectedPlan.name}` : 'Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChoosePlanModal;
